import { BaseService } from "../../core/baseService";
import {
  ServiceTypeDefinitionDto,
  CreateServiceTypeDefinitionDto,
  UpdateServiceTypeDefinitionDto,
  ReorderServiceTypesDto,
  ServiceIconDto,
  ServiceTypeTemplateDto,
  ImportServiceTypeTemplatesResultDto,
} from "../types/serviceType.types";
import { ApiResult } from "../../core/types";

export interface ServiceTypeServiceInterface {
  getAll(includeInactive?: boolean): Promise<ApiResult<ServiceTypeDefinitionDto[]>>;
  getById(id: string): Promise<ApiResult<ServiceTypeDefinitionDto>>;
  create(data: CreateServiceTypeDefinitionDto): Promise<ApiResult<ServiceTypeDefinitionDto>>;
  update(id: string, data: UpdateServiceTypeDefinitionDto): Promise<ApiResult<void>>;
  delete(id: string): Promise<ApiResult<void>>;
  reorder(data: ReorderServiceTypesDto): Promise<ApiResult<void>>;
  toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>>;
  seedDefaults(): Promise<ApiResult<{ message: string }>>;
  getAvailableIcons(): Promise<ApiResult<ServiceIconDto[]>>;
  uploadServiceTypeImage(id: string, file: File): Promise<ApiResult<{ imageUrl: string }>>;
  removeServiceTypeImage(id: string): Promise<ApiResult<void>>;
  getTemplates(): Promise<ApiResult<ServiceTypeTemplateDto[]>>;
  importTemplates(codes: string[]): Promise<ApiResult<ImportServiceTypeTemplatesResultDto>>;
}

export class ServiceTypeService extends BaseService implements ServiceTypeServiceInterface {
  async getAll(includeInactive = false): Promise<ApiResult<ServiceTypeDefinitionDto[]>> {
    return this.handleRequest<ServiceTypeDefinitionDto[]>(
      this.client.get("/api/admin/service-types", {
        params: { includeInactive },
      })
    );
  }

  async getById(id: string): Promise<ApiResult<ServiceTypeDefinitionDto>> {
    return this.handleRequest<ServiceTypeDefinitionDto>(
      this.client.get(`/api/admin/service-types/${id}`)
    );
  }

  async create(data: CreateServiceTypeDefinitionDto): Promise<ApiResult<ServiceTypeDefinitionDto>> {
    return this.handleRequest<ServiceTypeDefinitionDto>(
      this.client.post("/api/admin/service-types", data)
    );
  }

  async update(id: string, data: UpdateServiceTypeDefinitionDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/service-types/${id}`, data)
    );
  }

  async delete(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-types/${id}`)
    );
  }

  async reorder(data: ReorderServiceTypesDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put("/api/admin/service-types/reorder", data)
    );
  }

  async toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.patch(`/api/admin/service-types/${id}/toggle`, null, {
        params: { isActive },
      })
    );
  }

  async seedDefaults(): Promise<ApiResult<{ message: string }>> {
    return this.handleRequest<{ message: string }>(
      this.client.post("/api/admin/service-types/seed-defaults")
    );
  }

  async getAvailableIcons(): Promise<ApiResult<ServiceIconDto[]>> {
    return this.handleRequest<ServiceIconDto[]>(
      this.client.get("/api/admin/service-types/icons")
    );
  }

  async uploadServiceTypeImage(id: string, file: File): Promise<ApiResult<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append("file", file);
    return this.handleRequest<{ imageUrl: string }>(
      this.client.post(`/api/admin/service-types/${id}/image`, formData, {
        transformRequest: [(data: FormData, headers: Record<string, string>) => {
          delete headers["Content-Type"];
          return data;
        }],
      })
    );
  }

  async removeServiceTypeImage(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-types/${id}/image`)
    );
  }

  async getTemplates(): Promise<ApiResult<ServiceTypeTemplateDto[]>> {
    return this.handleRequest<ServiceTypeTemplateDto[]>(
      this.client.get("/api/admin/service-types/templates")
    );
  }

  async importTemplates(codes: string[]): Promise<ApiResult<ImportServiceTypeTemplatesResultDto>> {
    return this.handleRequest<ImportServiceTypeTemplatesResultDto>(
      this.client.post("/api/admin/service-types/templates/import", { codes })
    );
  }
}
